/**
 * Structured API error response format matching server-side error responses.
 */
export interface ApiErrorResponse {
    error: string;
    code: string;
    field?: string;
    details?: unknown;
}

/**
 * Base class for all API errors. Preserves structured error data from the server.
 */
export class ApiError extends Error {
    constructor(
        message: string,
        public readonly code: string,
        public readonly field?: string,
        public readonly details?: unknown,
        public readonly status?: number,
    ) {
        super(message);
        this.name = 'ApiError';
    }

    /**
     * Creates an ApiError from a server response body.
     */
    static fromResponse(body: ApiErrorResponse, status?: number): ApiError {
        return new ApiError(body.error, body.code, body.field, body.details, status);
    }
}

/**
 * Thrown when input validation fails (400 status).
 */
export class ValidationError extends ApiError {
    constructor(message: string, field?: string, details?: unknown) {
        super(message, 'validationError', field, details, 400);
        this.name = 'ValidationError';
    }
}

/**
 * Thrown when a resource is not found (404 status).
 */
export class NotFoundError extends ApiError {
    constructor(message: string) {
        super(message, 'notFound', undefined, undefined, 404);
        this.name = 'NotFoundError';
    }
}

/**
 * Thrown when authentication is required or invalid (401 status).
 */
export class UnauthorizedError extends ApiError {
    constructor(message: string = 'Authentication required') {
        super(message, 'unauthorized', undefined, undefined, 401);
        this.name = 'UnauthorizedError';
    }
}

/**
 * Thrown when permission is denied (403 status).
 */
export class ForbiddenError extends ApiError {
    constructor(message: string = 'Permission denied') {
        super(message, 'forbidden', undefined, undefined, 403);
        this.name = 'ForbiddenError';
    }
}
